import { request, PageResult } from './request';
import { buildToPath, sha256Hex, uploadToStorage } from '../utils/upload';

// (2026-08-14 口径变更) 效果图资产 = 工艺参数行内嵌的效果图/矩阵图
// (源头 material_process_para + para_matrices), /v1/gallery 由后端跨 param_<arch> 表实时聚合;
// 资产的增删改走参数行编辑, 本模块只读.

export interface Gallery {
    /** 所属 param_<arch> 参数行 id; 仅在 (archCode, kind) 内唯一 */
    id: number;
    archCode: string;
    deviceKey: string;
    xcode?: string;
    /** 光源码 (由 device_key 推导, e.g. blue/co2/ir/uv) */
    lightSource?: string;
    /** 加工类型 (参数行 sub_process) */
    processType?: string;
    /** effect = 效果图 / matrix = 矩阵图 (同一参数行两者兼有时各出一条) */
    kind: 'effect' | 'matrix';
    /** 矩阵元数据 JSON string (参数行 paraMatrix); 效果图条目为空 */
    matrixJson?: string;
    /** 来源参数行 (= archCode / id), paramJumpPath 精确跳转用 */
    linkedArchCode?: string;
    linkedParamRowId?: number;
    /** 图片地址: 效果图 = effect_image, 矩阵图 = paraMatrix.frontImg (源 CDN 外链) */
    imageUrl?: string;
    updatedAt?: number;
}

export interface Attachment {
    id: number;
    uuid: string;
    objectKey: string;
    bucket: string;
    fileName: string;
    mime: string;
    sizeBytes: number;
    sha256: string;
    /** 永久 CDN 地址 (https://static-res.atomm.com/...), 与 fileUrl 相同 */
    url: string;
    /** 永久 CDN 地址 — 推荐写库 (<img src>) */
    fileUrl: string;
    /** true 表示该文件 sha256 命中已有 attachment, 服务端复用了旧记录 */
    reused?: boolean;
}

export interface ListGalleryParams {
    /** 模糊搜 xcode / device_key / note */
    keyword?: string;
    archCode?: string;
    deviceKey?: string;
    /** 机型 (e.g. S1, F2, P2); 跟 deviceKey 二选一, 后端 sub-query 反查 device.family. */
    family?: string;
    xcode?: string;
    lightSource?: string;
    processType?: string;
    priority?: string;
    /** yes = 只看矩阵图 (matrixJson 非空) / no = 只看普通效果图, 空 = 全部 */
    hasMatrix?: string;
    /** 按所属材料一/二级分类过滤 */
    type1?: string;
    type2?: string;
    page?: number;
    pageSize?: number;
}

const BASE = '/v1';

export function listGallery(params: ListGalleryParams): Promise<PageResult<Gallery>> {
    return request<PageResult<Gallery>>(`${BASE}/gallery`, { query: params });
}

// listGalleryFacets archCode 限定 (空串=全部) 下出现过的 deviceKey / processType / lightSource 全集.
// 用于"设备/机型"/"加工类型"/"激光类型"下拉候选, 不受分页影响.
export function listGalleryFacets(
    archCode?: string,
): Promise<{ deviceKeys: string[]; processTypes: string[]; lightSources: string[] }> {
    return request(`${BASE}/gallery/facets`, { query: { archCode: archCode || '' } });
}

// (2026-08-14) getGallery/createGallery/updateGallery/deleteGallery/importGalleryXlsx 已下线:
// 资产聚合自参数行, 增删改与导入统一走参数行编辑/导入.

/**
 * 效果图/矩阵图 → 工艺参数 的跳转路径:
 * 有精确来源参数行 (linkedArchCode/linkedParamRowId) → 参数编辑页;
 * 否则按 材质 × 设备 × 加工类型 条件跳参数总览筛选.
 * subProcess 优先用调用方从 matrixJson 解析出的 processingType (比 gallery.processType 更准).
 */
export function paramJumpPath(g: Gallery, subProcess?: string): string {
    if (g.linkedParamRowId && g.linkedArchCode) {
        return `/param-sets/manage?arch=${encodeURIComponent(g.linkedArchCode)}&id=${g.linkedParamRowId}`;
    }
    const sp = subProcess || g.processType || '';
    return (
        `/param-sets?arch=${encodeURIComponent(g.archCode)}` +
        (g.xcode ? `&xcode=${encodeURIComponent(g.xcode)}` : '') +
        (g.deviceKey ? `&deviceKey=${encodeURIComponent(g.deviceKey)}` : '') +
        (sp ? `&subProcess=${encodeURIComponent(sp)}` : '')
    );
}

// mimeByExt file.type 为空时按扩展名兜底 (后端只收 image/jpeg|png|webp|gif|svg+xml).
const mimeByExt: Record<string, string> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.gif': 'image/gif',
    '.svg': 'image/svg+xml',
};

/**
 * 上传用途 (后端 attachment purpose): 不传 = 通用图片 (≤20MB, SVG ≤512KB);
 * texture = 材料贴图 (≤50MB, 仅 jpg/png/svg/webp, 2026-10-08).
 */
export type UploadPurpose = 'texture';

// uploadAttachment 单张图片: 前端直传 R2 (utils/upload.ts) 拿 CDN 永久地址,
// 再 POST /v1/attachments 注册元数据 (后端 sha256 去重, 命中复用旧记录 reused=true).
export async function uploadAttachment(file: File, purpose?: UploadPurpose): Promise<Attachment> {
    let mime = file.type;
    if (!mime) {
        const dot = file.name.lastIndexOf('.');
        mime = mimeByExt[dot >= 0 ? file.name.slice(dot).toLowerCase() : ''] || '';
    }
    const sha256 = await sha256Hex(file);
    const url = await uploadToStorage(file, buildToPath(file.name, purpose || 'gallery'));
    return request<Attachment>(`${BASE}/attachments`, {
        method: 'POST',
        body: { url, fileName: file.name, mime, sizeBytes: file.size, sha256, purpose },
    });
}

// getAttachmentURL 按 id 换永久 URL (公共读 CDN 后无过期, expiresAt 恒 0).
export function getAttachmentURL(id: number): Promise<{ id: number; objectKey: string; url: string; expiresAt: number }> {
    return request(`${BASE}/attachments/${id}/url`);
}
